import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Activity,
  Bot,
  Clock,
  DollarSign,
  Layers,
  Loader2,
  MessageSquare,
  Settings2,
  ArrowLeft,
  Plug,
  TrendingUp,
  Download,
  FileJson,
  FileText,
  Webhook,
  CheckCircle2,
  XCircle,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getAgentMetrics,
  agentJsonUrl,
  agentMarkdownUrl,
  agentPdfUrl,
  listAgentEvents,
} from "@/lib/api";

const WINDOWS = [
  { v: 1, label: "1 mes" },
  { v: 3, label: "3 meses" },
  { v: 6, label: "6 meses" },
  { v: 9, label: "9 meses" },
  { v: 12, label: "12 meses" },
];
const MONTH_LABEL = (k) => {
  const [y, m] = k.split("-");
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleString("es-419", { month: "short" }).replace(".", "") + " " + y.slice(2);
};

export default function AgentMetrics() {
  const { slug } = useParams();
  const [window, setWindow] = useState(() => Number(new URLSearchParams(globalThis.location.search).get("ventana")) || 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [d, ev] = await Promise.all([
          getAgentMetrics(slug, window),
          listAgentEvents(slug, 50).catch(() => null),
        ]);
        if (!cancelled) {
          setData(d);
          setEvents(ev);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, window]);

  const trend = useMemo(
    () => (data?.metrics?.trend || []).map((t) => ({ ...t, label: MONTH_LABEL(t.month) })),
    [data]
  );
  const topPrompts = (data?.metrics?.top_prompts || []).filter((p) => p.title);
  const agent = data?.agent;
  const m = data?.metrics?.totals || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white text-slate-900" data-testid="agent-metrics-page">
      <TopNav
        right={
          agent && (
            <div className="flex items-center gap-2">
              <a href={agentJsonUrl(slug)} download data-testid="metrics-export-json">
                <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                  <FileJson className="mr-2 h-3.5 w-3.5" /> JSON
                </Button>
              </a>
              <a href={agentMarkdownUrl(slug)} download data-testid="metrics-export-md">
                <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                  <FileText className="mr-2 h-3.5 w-3.5" /> MD
                </Button>
              </a>
              <a href={agentPdfUrl(slug)} download data-testid="metrics-export-pdf">
                <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                  <Download className="mr-2 h-3.5 w-3.5" /> PDF
                </Button>
              </a>
              <Link to={`/agentes/${slug}`}>
                <Button variant="outline" className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.2em] text-slate-700 hover:bg-slate-100">
                  Abrir
                </Button>
              </Link>
              <Link to={`/agentes/${slug}/editar`}>
                <Button className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-slate-800" data-testid="edit-from-metrics-btn">
                  <Settings2 className="mr-2 h-3.5 w-3.5" /> Editar
                </Button>
              </Link>
            </div>
          )
        }
      />

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 hover:text-slate-900"
          data-testid="back-to-dashboard-link"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Dashboard
        </Link>

        {/* Header */}
        <div className="mt-6 flex flex-wrap items-end justify-between gap-6 border-b border-slate-200 pb-8">
          <div>
            {agent && (
              <>
                <p className="eyebrow text-accent-blue">{agent.department}</p>
                <h1 className="mt-3 flex items-center gap-3 font-display text-4xl font-medium text-slate-900">
                  <span className="inline-flex h-10 w-10 items-center justify-center bg-slate-900 text-accent-blue-light">
                    <Bot className="h-5 w-5" />
                  </span>
                  {agent.name}
                </h1>
                <p className="mt-3 max-w-2xl text-sm text-slate-600">
                  {agent.description}
                </p>
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Ventana</span>
            <Select value={String(window)} onValueChange={(v) => setWindow(Number(v))}>
              <SelectTrigger className="h-10 w-[150px] rounded-sm border-slate-300 bg-white text-xs uppercase tracking-[0.18em]" data-testid="window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOWS.map((w) => (
                  <SelectItem key={w.v} value={String(w.v)} data-testid={`window-opt-${w.v}`}>
                    {w.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPI tiles */}
        <section className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="agent-kpi-tiles">
          <Kpi icon={MessageSquare} label="Interacciones" value={m.chats ?? 0} accent="teal" />
          <Kpi icon={Activity} label="Mensajes" value={m.messages ?? 0} />
          <Kpi icon={Clock} label="Horas ahorradas" value={(m.hours_saved ?? 0).toLocaleString("es-419")} suffix="h" accent="teal" />
          <Kpi icon={DollarSign} label="Ahorro USD" value={(m.usd_saved ?? 0).toLocaleString("es-419", { maximumFractionDigits: 0 })} prefix="$" accent="teal" />
          <Kpi icon={Layers} label="Starters usados" value={`${m.starters_used ?? 0}/${m.starters_total ?? 0}`} />
          <Kpi icon={TrendingUp} label="Cobertura" value={Math.round(m.starter_coverage_pct ?? 0)} suffix="%" />
        </section>

        {/* Real-event KPIs (from signed webhook) */}
        <section className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="real-kpi-tiles">
          <Kpi icon={CheckCircle2} label="Tareas completadas" value={m.tasks_completed ?? 0} accent="teal" />
          <Kpi icon={XCircle} label="Tareas fallidas" value={m.tasks_failed ?? 0} />
          <Kpi icon={TrendingUp} label="Tasa de éxito" value={Math.round(m.success_rate_pct ?? 0)} suffix="%" accent="teal" />
          <Kpi icon={ThumbsUp} label="Feedback +" value={m.feedback_positive ?? 0} accent="teal" />
          <Kpi icon={ThumbsDown} label="Feedback −" value={m.feedback_negative ?? 0} />
          <Kpi icon={Activity} label="CSAT" value={Math.round(m.csat_pct ?? 0)} suffix="%" />
        </section>

        {/* Trend + Tools */}
        <section className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Card title="Uso a lo largo del tiempo" subtitle={`Ventana ${window} ${window === 1 ? "mes" : "meses"}`}>
              <div className="h-[260px]" data-testid="agent-trend-chart">
                {loading ? (
                  <Spinner />
                ) : (
                  <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
                    <AreaChart data={trend} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="aChats" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0D9488" stopOpacity={0.4} />
                          <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="2 4" stroke="#E2E8F0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} width={36} />
                      <ReTooltip
                        contentStyle={{ background: "#0F172A", border: "none", borderRadius: 2, color: "#F8FAFC", fontSize: 12 }}
                      />
                      <Area type="monotone" dataKey="chats" name="Interacciones" stroke="#0D9488" strokeWidth={2} fill="url(#aChats)" />
                      <Area type="monotone" dataKey="messages" name="Mensajes" stroke="#0F172A" strokeWidth={1.5} fillOpacity={0} />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </Card>
          </div>
          <div className="lg:col-span-4">
            <Card title="Herramientas conectadas" subtitle="MCP MOCKED · Prototipo">
              {!agent ? (
                <Spinner />
              ) : (
                <div className="flex flex-wrap gap-1.5 py-2" data-testid="agent-tools-pills">
                  {(agent.tools || [])
                    .filter((t) => t.enabled)
                    .map((t) => (
                      <span
                        key={t.id}
                        className="inline-flex items-center gap-1 border border-teal-700/40 bg-teal-50 px-2 py-1 text-[11px] uppercase tracking-[0.12em] text-teal-800"
                      >
                        <Plug className="h-3 w-3" /> {t.label}
                      </span>
                    ))}
                  {(agent.tools || []).filter((t) => t.enabled).length === 0 && (
                    <p className="italic text-slate-500">Sin herramientas conectadas.</p>
                  )}
                </div>
              )}
              {agent && (
                <div className="mt-5 border-t border-slate-100 pt-4 text-[11px] text-slate-500">
                  <p>Última actividad: <span className="font-mono text-slate-700">{data?.metrics?.totals?.last_used ? new Date(data.metrics.totals.last_used).toLocaleString("es-419") : "—"}</span></p>
                  <p className="mt-1">Configuración:</p>
                  <ul className="mt-2 space-y-1 font-mono">
                    <li>{agent.metrics_config?.minutes_per_interaction} min/interacción</li>
                    <li>${agent.metrics_config?.hourly_rate_usd} USD/hora</li>
                    <li>Ventana base: {agent.metrics_config?.metrics_window_months} mes(es)</li>
                  </ul>
                </div>
              )}
            </Card>
          </div>
        </section>

        {/* Top prompts */}
        <section className="mt-6">
          <Card title="Iniciadores más usados" subtitle="Conteo de invocaciones por prompt pre-creado">
            {loading ? (
              <Spinner />
            ) : topPrompts.length === 0 ? (
              <p className="py-6 text-center text-sm italic text-slate-500">Sin invocaciones de iniciadores en la ventana seleccionada.</p>
            ) : (
              <div className="h-[300px]" data-testid="top-prompts-chart">
                <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
                  <BarChart data={topPrompts} layout="vertical" margin={{ top: 5, right: 40, left: 12, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="2 4" horizontal={false} stroke="#E2E8F0" />
                    <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="title" width={210} tick={{ fontSize: 12, fill: "#0F172A" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
                    <ReTooltip
                      cursor={{ fill: "rgba(15,23,42,0.04)" }}
                      contentStyle={{ background: "#0F172A", border: "none", borderRadius: 2, color: "#F8FAFC", fontSize: 12 }}
                    />
                    <Bar dataKey="count" barSize={16}>
                      {topPrompts.map((_, i) => (
                        <Cell key={i} fill={i === 0 ? "#0D9488" : "#0F172A"} />
                      ))}
                      <LabelList dataKey="count" position="right" style={{ fill: "#0F172A", fontSize: 11, fontWeight: 600 }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        </section>

        {/* Recent webhook events */}
        <section className="mt-6">
          <Card title="Eventos recientes" subtitle="Eventos firmados recibidos vía webhook">
            {!events ? (
              <Spinner />
            ) : (events.events || []).length === 0 ? (
              <div className="py-8 text-center">
                <Webhook className="mx-auto h-7 w-7 text-slate-300" />
                <p className="mt-3 text-sm italic text-slate-500">
                  Sin eventos recibidos todavía.
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Configure el webhook en el{" "}
                  <a className="text-accent-blue font-semibold hover:underline" href={`/agentes/${slug}/editar`}>
                    editor del agente →
                  </a>{" "}
                  para empezar a recibir tareas completadas desde sus sistemas.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="events-table-wrapper">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      <th className="px-3 py-2">Cuando</th>
                      <th className="px-3 py-2">Evento</th>
                      <th className="px-3 py-2">Prompt</th>
                      <th className="px-3 py-2">Metadata</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(events.events || []).map((e) => (
                      <tr key={e.id} className="border-b border-slate-100">
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">
                          {new Date(e.ts).toLocaleString("es-419")}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] ${
                            e.event_type === "task.completed" ? "border-teal-700/40 bg-teal-50 text-teal-800" :
                            e.event_type === "task.failed" ? "border-rose-300 bg-rose-50 text-rose-700" :
                            e.event_type === "feedback.positive" ? "border-teal-700/40 bg-teal-50 text-teal-800" :
                            e.event_type === "feedback.negative" ? "border-amber-300 bg-amber-50 text-amber-800" :
                            "border-slate-300 bg-slate-50 text-slate-700"
                          }`}>
                            {e.event_type}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-700">
                          {e.prompt_id || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-600 max-w-md truncate">
                          {Object.keys(e.metadata || {}).length === 0
                            ? "—"
                            : JSON.stringify(e.metadata).slice(0, 200)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </section>

        <p className="mt-12 text-[11px] uppercase tracking-[0.18em] text-amber-700">
          Estimaciones basadas en: {agent?.metrics_config?.minutes_per_interaction || 15} min / interacción ·
          tarifa USD {agent?.metrics_config?.hourly_rate_usd || 25}/hora.
          Configurable desde el editor del agente.
        </p>
      </main>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, prefix, suffix, accent = "navy" }) {
  const dot = accent === "teal" ? "bg-teal-600" : "bg-slate-900";
  return (
    <div className="relative overflow-hidden border border-slate-200 bg-white p-5">
      <div className="flex items-start justify-between">
        <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.7} />
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      </div>
      <p className="mt-5 font-display text-3xl font-semibold leading-none text-slate-900">
        {prefix || ""}{value}{suffix || ""}
      </p>
      <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">{label}</p>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="border border-slate-200 bg-white p-6">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <h3 className="font-display text-lg font-medium text-slate-900">{title}</h3>
          {subtitle && <p className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-slate-500">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex h-[200px] items-center justify-center text-slate-400">
      <Loader2 className="h-5 w-5 animate-spin" />
    </div>
  );
}
